import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Download, FileText, Check, Clock, ChevronUp, ChevronDown } from 'lucide-react';
import api from '../../services/api';
import ReportFilters, { getDefaultDateRange } from './ReportFilters';

const PaymentDetailReport = () => {
  const [data, setData] = useState({ results: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    ...getDefaultDateRange(),
    supplier: '',
    cost_centre: '',
    paid_status: '',
  });
  
  // Sorting state
  const [sortField, setSortField] = useState('payment_date');
  const [sortDirection, setSortDirection] = useState('desc');

  const fetchData = async (appliedFilters = filters) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (appliedFilters.date_from) params.append('date_from', appliedFilters.date_from);
      if (appliedFilters.date_to) params.append('date_to', appliedFilters.date_to);
      if (appliedFilters.supplier) params.append('supplier', appliedFilters.supplier);
      if (appliedFilters.cost_centre) params.append('cost_centre', appliedFilters.cost_centre);
      if (appliedFilters.paid_status) params.append('paid_status', appliedFilters.paid_status);
      
      const response = await api.get(`/reports/payment-detail/?${params.toString()}`);
      const responseData = response.data;
      setData({
        results: Array.isArray(responseData) ? responseData : (responseData.results || []),
        totals: responseData.totals || {}
      });
    } catch (err) {
      setError('Failed to load report data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    fetchData(filters);
  };

  const handleClearFilters = () => {
    const defaultFilters = {
      ...getDefaultDateRange(),
      supplier: '',
      cost_centre: '',
      paid_status: '',
    };
    setFilters(defaultFilters);
    fetchData(defaultFilters);
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.supplier) params.append('supplier', filters.supplier);
      if (filters.cost_centre) params.append('cost_centre', filters.cost_centre);
      if (filters.paid_status) params.append('paid_status', filters.paid_status);
      
      const response = await api.get(`/reports/payment-detail/export/?${params.toString()}`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payment_detail_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export report');
    }
  };

  // Handle column sort
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending (except dates default to desc)
      setSortField(field);
      setSortDirection(field.includes('date') || field === 'paid_at' ? 'desc' : 'asc');
    }
  };

  // Sort the data
  const sortedData = useMemo(() => {
    if (!data.results || data.results.length === 0) return [];
    
    return [...data.results].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle null/undefined values
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      
      // Handle different data types
      if (sortField === 'amount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortField === 'payment_date' || sortField === 'paid_at') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else if (sortField === 'is_paid') {
        aVal = aVal ? 1 : 0;
        bVal = bVal ? 1 : 0;
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      // Compare
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data.results, sortField, sortDirection]);

  // Render sort indicator
  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <span className="sort-icon sort-icon-inactive">⇅</span>;
    }
    return sortDirection === 'asc' 
      ? <ChevronUp size={14} className="sort-icon" />
      : <ChevronDown size={14} className="sort-icon" />;
  };

  // Sortable header component
  const SortableHeader = ({ field, children, className = '' }) => (
    <th 
      className={`sortable-header ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="header-content">
        {children}
        {renderSortIcon(field)}
      </div>
    </th>
  );

  const formatAmount = (amount) => {
    return parseFloat(amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-ZA');
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-ZA', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  };

  // Render the total amount based on single vs mixed currencies
  const renderTotalAmount = () => {
    const { totals } = data;
    
    if (!totals || totals.total_payments === 0) {
      return <div className="stat-value">0.00</div>;
    }
    
    if (!totals.is_mixed) {
      // Single currency - show with badge
      return (
        <div className="currency-breakdown-inline">
          <div className="breakdown-row">
            <span className="breakdown-currency-tag">{totals.currency || 'ZAR'}</span>
            <span>{formatAmount(totals.grand_total)}</span>
          </div>
        </div>
      );
    }
    
    // Mixed currencies - show breakdown
    return (
      <div className="currency-breakdown-inline">
        {totals.currency_breakdown?.map((item, index) => (
          <div key={index} className="breakdown-row">
            <span className="breakdown-currency-tag">{item.currency}</span>
            <span>{formatAmount(item.total)}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="report-container">
      <div className="report-header">
        <h1>Payment Detail Report</h1>
        <button className="btn btn-primary" onClick={handleExport} disabled={loading}>
          <Download size={18} />
          Export to Excel
        </button>
      </div>

      <div className="card">
        <ReportFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
        />
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="card">
        {/* Summary Stats */}
        <div className="report-stats">
          <div className="stat-card stat-card-wide">
            <div className="stat-label">Total Amount</div>
            {renderTotalAmount()}
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Payments</div>
            <div className="stat-value">{data.totals?.total_payments || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Paid</div>
            <div className="stat-value success">{data.totals?.total_paid || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Unpaid</div>
            <div className="stat-value warning">{data.totals?.total_unpaid || 0}</div>
          </div>
        </div>

        {/* Data Table */}
        {loading ? (
          <div className="loading">Loading report data...</div>
        ) : data.results.length === 0 ? (
          <div className="empty-state">No data found for the selected filters</div>
        ) : (
          <div className="table-responsive">
            <table className="report-table sortable-table">
              <thead>
                <tr>
                  <SortableHeader field="payment_date">Payment Date</SortableHeader>
                  <SortableHeader field="cr_account_name">Supplier</SortableHeader>
                  <SortableHeader field="currency" className="text-center">Currency</SortableHeader>
                  <SortableHeader field="amount" className="text-right">Amount</SortableHeader>
                  <SortableHeader field="cr_statement_ref">Reference</SortableHeader>
                  <SortableHeader field="batch_reference">Batch</SortableHeader>
                  <SortableHeader field="cost_centre_code">Cost Centre</SortableHeader>
                  <SortableHeader field="is_paid" className="text-center">Status</SortableHeader>
                  <SortableHeader field="paid_at">Paid Date</SortableHeader>
                  <th className="text-center">Invoice</th>
                  <th className="text-center">POP</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDate(row.payment_date)}</td>
                    <td>{row.cr_account_name}</td>
                    <td className="text-center">
                      <span className="breakdown-currency-tag">{row.currency}</span>
                    </td>
                    <td className="text-right">{formatAmount(row.amount)}</td>
                    <td className="text-muted">{row.cr_statement_ref}</td>
                    <td>
                      {row.batch_id ? (
                        <Link to={`/batches/${row.batch_id}`} className="batch-link">
                          {row.batch_reference}
                        </Link>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {row.cost_centre_code ? (
                        <span className="code-badge">{row.cost_centre_code}</span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="text-center">
                      {row.is_paid ? (
                        <span className="badge badge-success">
                          <Check size={12} /> Paid
                        </span>
                      ) : (
                        <span className="badge badge-warning">
                          <Clock size={12} /> Pending
                        </span>
                      )}
                    </td>
                    <td className="text-muted">{formatDateTime(row.paid_at)}</td>
                    <td className="text-center">
                      {row.invoice ? (
                        <a 
                          href={row.invoice} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="pop-link"
                          title="View Invoice"
                        >
                          <FileText size={16} />
                        </a>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="text-center">
                      {row.proof_of_payment ? (
                        <a 
                          href={row.proof_of_payment} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="pop-link"
                          title="View Proof of Payment"
                        >
                          <FileText size={16} />
                        </a>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td><strong>Total</strong></td>
                  <td><strong>{data.totals?.total_payments} payments</strong></td>
                  <td></td>
                  <td className="text-right">
                    {data.totals?.is_mixed ? (
                      <em className="text-muted">Mixed</em>
                    ) : (
                      <strong>{formatAmount(data.totals?.grand_total)}</strong>
                    )}
                  </td>
                  <td colSpan="7"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentDetailReport;